import { SearchIcon } from "lucide-react";
import { useState } from "react";
import type { AddCmmdProjectMemberParams, CmmdMember } from "@cmmd-forge/contracts";

import { PersonAvatar } from "~/components/identity/PersonAvatar";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/utils";

export interface AddProjectPersonDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly projectMembers: readonly CmmdMember[];
  readonly workspaceMembers: readonly CmmdMember[];
  readonly loading: boolean;
  readonly loadError: string | null;
  readonly onAdd: (input: AddCmmdProjectMemberParams) => Promise<void>;
}

export function AddProjectPersonDialog(props: AddProjectPersonDialogProps) {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const workspaceMembers = props.workspaceMembers ?? [];
  const eligible = filterEligibleProjectPeople(workspaceMembers, props.projectMembers, query);

  const submit = async () => {
    const userId = Number(selectedId);
    if (!Number.isInteger(userId) || userId <= 0) return;
    setSaving(true);
    setError(null);
    try {
      await props.onAdd({ userId, role: "member" });
      setSelectedId(null);
      setQuery("");
      props.onOpenChange(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "This person could not be added.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogPopup className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add person</DialogTitle>
          <DialogDescription>
            Add an existing CMMD workspace member to this project. This does not invite a new user.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-3">
          <label className="relative block">
            <SearchIcon
              className="pointer-events-none absolute left-3 top-2.5 size-4 text-muted-foreground"
              aria-hidden
            />
            <span className="sr-only">Search workspace people</span>
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name or email"
              className="pl-9"
              autoFocus
            />
          </label>
          {props.loading ? (
            <p
              role="status"
              className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground"
            >
              Loading workspace people…
            </p>
          ) : props.loadError ? (
            <p
              role="alert"
              className="rounded-lg border border-destructive-border p-4 text-sm text-destructive"
            >
              Workspace people could not be loaded: {props.loadError}
            </p>
          ) : eligible.length === 0 ? (
            <p className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
              {workspaceMembers.length === props.projectMembers.length
                ? "Everyone in this project is already on the project."
                : "No eligible people match this search."}
            </p>
          ) : (
            <div className="max-h-72 overflow-y-auto rounded-lg border border-border">
              {eligible.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => setSelectedId(member.id)}
                  className={cn(
                    "flex min-h-14 w-full items-center gap-3 border-b border-border px-3 py-2 text-left last:border-b-0 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                    selectedId === member.id ? "bg-accent" : null,
                  )}
                >
                  <PersonAvatar
                    name={member.name}
                    avatarUrl={member.avatarUrl}
                    className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-xs font-medium text-muted-foreground"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {member.name}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {member.email ?? "CMMD workspace member"}
                    </span>
                  </span>
                  <span className="text-xs capitalize text-muted-foreground">
                    {member.role ?? "member"}
                  </span>
                </button>
              ))}
            </div>
          )}
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </DialogPanel>
        <DialogFooter>
          <Button variant="ghost" onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={saving || selectedId === null} onClick={() => void submit()}>
            {saving ? "Adding…" : "Add to project"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

export function filterEligibleProjectPeople(
  workspaceMembers: readonly CmmdMember[],
  projectMembers: readonly CmmdMember[],
  query: string,
): readonly CmmdMember[] {
  const existingIds = new Set(projectMembers.map((member) => member.id));
  const needle = query.trim().toLowerCase();
  return workspaceMembers.filter(
    (member) =>
      !existingIds.has(member.id) &&
      (needle.length === 0 ||
        `${member.name} ${member.email ?? ""}`.toLowerCase().includes(needle)),
  );
}
